import { Rate, Typography } from 'antd'
import type { ReactNode } from 'react'
import { MdStar } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'

import styles from './RatingsTable.module.css'

const { Text } = Typography

/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
interface RatingsTableProps {
    ratings: any[]
    /** Підпис рядка після дати — напр. водій і завдання, коли оцінки з кількох завдань (заявка) */
    rowLabel?: ((rating: any) => ReactNode) | undefined
}

const ratingTime = (rating: any): number => {
    const time = new Date(rating?.ratingDate ?? '').getTime()
    return Number.isNaN(time) ? 0 : time
}

const formatDate = (value: unknown): string =>
    typeof value === 'string' && value !== '' && !Number.isNaN(new Date(value).getTime())
        ? new Date(value).toLocaleDateString('uk-UA')
        : '—'

/**
 * Оцінки водія за дні роботи (ставить диспетчер при завершенні задачі): зведення з
 * середньою оцінкою і рядки «дата · зірки · коментар», новіші зверху.
 */
export const RatingsTable = ({ ratings, rowLabel }: RatingsTableProps) => {
    if (ratings.length === 0) {
        return <Text type="secondary">Оцінок немає</Text>
    }

    const scores = ratings.map((item) => Number(item?.rating)).filter((score) => Number.isFinite(score))
    const average = scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null
    const sorted = [...ratings].sort((a, b) => ratingTime(b) - ratingTime(a))

    return (
        <div className={cx(styles, 'root')}>
            <div className={cx(styles, 'summary')}>
                {average !== null && (
                    <span className={cx(styles, 'average')}>
                        <MdStar className={cx(styles, 'averageStar')} aria-hidden="true" />
                        {average.toFixed(1)}
                    </span>
                )}
                <span className={cx(styles, 'count')}>
                    {average !== null ? 'середня · ' : ''}
                    {ratings.length} {pluralizeUk(ratings.length, ['оцінка', 'оцінки', 'оцінок'])}
                </span>
            </div>

            <ul className={cx(styles, 'list')}>
                {sorted.map((item: any, index: number) => (
                    <li
                        key={item.uuid || index}
                        className={`${cx(styles, 'row')}${rowLabel ? ` ${cx(styles, 'rowLabeled')}` : ''}`}
                    >
                        <span className={cx(styles, 'date')}>{formatDate(item.ratingDate)}</span>
                        {rowLabel && <span className={cx(styles, 'label')}>{rowLabel(item)}</span>}
                        <span className={cx(styles, 'stars')}>
                            <Rate disabled value={Number(item.rating) || 0} count={5} />
                            <span className={cx(styles, 'score')}>{item.rating ?? '—'}/5</span>
                        </span>
                        <p className={cx(styles, 'comment')}>
                            {item.comment || <span className={cx(styles, 'empty')}>Без коментаря</span>}
                        </p>
                    </li>
                ))}
            </ul>
        </div>
    )
}
